import { useState, useEffect } from 'react';
import { Sparkles, Info } from 'lucide-react';
import { VerdictBadge, type Verdict } from '@kodprep/platform-ui/atomic';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import type { SampleTestCase } from '@/lib/hooks/useTestCases';
import type { FunctionSignature } from '@/lib/api/problems';
import { labelledInputs } from './testcaseInputs';
import { PaneMaximizeButton, PaneCollapseButton } from './PaneControls';
import { diffRowSets, tryParseRowSet } from './sqlResultDiff';

/** Renders a diff cell value; strings render bare (no surrounding quotes). */
function formatCellValue(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

interface CaseResultItem {
  index: number;
  verdict: string;
  input: string;
  expected_output: string;
  actual_output: string;
  runtime_ms: number;
}

export interface SubmissionResult {
  submission_id: string;
  verdict: string;
  runtime_ms: number;
  memory_kb: number;
  passed_cases: number;
  total_cases: number;
  error_msg?: string;
  mode?: string;
  case_results?: CaseResultItem[];
  // Prompt mode only: JSON-encoded runner.PromptVerdict (score, per-criterion
  // pass/fail + note, model outputs) — see PromptModeResults below.
  output?: string;
}

// PromptVerdict mirrors services/judge/internal/runner.PromptVerdict, the
// JSON shape the judge writes into SubmissionResult.output for prompt-mode
// submissions.
interface PromptCriterionResult {
  id: string;
  description: string;
  pass: boolean;
  note: string;
}
interface PromptModelOutput {
  input: string;
  output: string;
}
interface PromptVerdict {
  score: number;
  max_score: number;
  criteria: PromptCriterionResult[];
  model_outputs: PromptModelOutput[];
}

interface TestCasePanelProps {
  testCases: SampleTestCase[];
  result: SubmissionResult | null;
  isJudging: boolean;
  mode: 'run' | 'submit' | null;
  executionMode?: 'stdio' | 'function' | 'sql' | 'prompt';
  /** Supplies real argument names for function-mode test cases. */
  signature?: FunctionSignature;
  isMaximized?: boolean;
  onToggleMaximize?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Rendered beneath an accepted verdict — the "what next" panel. */
  acceptedSlot?: React.ReactNode;
}

export function TestCasePanel({
  testCases,
  result,
  isJudging,
  mode,
  executionMode,
  signature,
  isMaximized,
  onToggleMaximize,
  isCollapsed,
  onToggleCollapse,
  acceptedSlot,
}: TestCasePanelProps) {
  // Prompt-mode problems have no per-case input/expected content in
  // test_cases (the actual test messages live on the problem's rubric and
  // are already shown above the prompt input) — showing this tab would just
  // be an empty, confusing box, so it's dropped entirely for prompt mode.
  const isPromptMode = executionMode === 'prompt';
  const [activeTab, setActiveTab] = useState<'testcase' | 'result'>(
    result || isPromptMode ? 'result' : 'testcase'
  );
  const [selectedCase, setSelectedCase] = useState(0);

  // Auto-switch to the Result tab when a run/submit starts or a new result lands,
  // but leave the user free to switch back to Testcase afterwards (a computed
  // override kept it stuck on Result once any result existed).
  useEffect(() => {
    if (isJudging) setActiveTab('result');
  }, [isJudging]);
  useEffect(() => {
    if (result) setActiveTab('result');
  }, [result]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => setActiveTab(v as 'testcase' | 'result')}
      className="flex h-full flex-col"
      aria-label="Test cases and results"
    >
      <TabsList className="h-auto w-full justify-start rounded-none border-b border-border bg-muted/30 px-3 py-0 shrink-0">
        {!isPromptMode && (
          <TabsTrigger
            value="testcase"
            className="rounded-none border-b-2 border-transparent px-3 py-2 text-sm font-medium data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none -mb-px"
          >
            Testcase
          </TabsTrigger>
        )}
        <TabsTrigger
          value="result"
          className="rounded-none border-b-2 border-transparent px-3 py-2 text-sm font-medium data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none -mb-px"
        >
          {isPromptMode ? 'Grading Result' : 'Test Result'}
        </TabsTrigger>
        {(onToggleCollapse || onToggleMaximize) && (
          <div className="ml-auto flex items-center pr-1">
            {onToggleCollapse && (
              <PaneCollapseButton isCollapsed={!!isCollapsed} onToggle={onToggleCollapse} />
            )}
            {onToggleMaximize && (
              <PaneMaximizeButton
                isMaximized={!!isMaximized}
                onToggle={onToggleMaximize}
                label="console"
              />
            )}
          </div>
        )}
      </TabsList>

      {!isPromptMode && (
        <TabsContent value="testcase" className="mt-0 flex-1 overflow-y-auto p-4 min-h-0">
          <TestcaseTab
            testCases={testCases}
            selectedCase={selectedCase}
            onSelectCase={setSelectedCase}
            executionMode={executionMode}
            signature={signature}
          />
        </TabsContent>
      )}

      <TabsContent value="result" className="mt-0 flex-1 overflow-y-auto p-4 min-h-0">
        <ResultTab
          result={result}
          isJudging={isJudging}
          mode={mode}
          executionMode={executionMode}
          acceptedSlot={acceptedSlot}
        />
      </TabsContent>
    </Tabs>
  );
}

function TestcaseTab({
  testCases,
  selectedCase,
  onSelectCase,
  executionMode,
  signature,
}: {
  testCases: SampleTestCase[];
  selectedCase: number;
  onSelectCase: (i: number) => void;
  executionMode?: string;
  signature?: FunctionSignature;
}) {
  if (testCases.length === 0) {
    return <p className="text-sm text-muted-foreground">No sample test cases available.</p>;
  }

  const tc = testCases[selectedCase];
  const inputs = labelledInputs(tc?.input ?? '', { executionMode, signature });

  return (
    <div>
      {/* Case selector pills. The active one is filled rather than tinted:
          at bg-muted against a muted-foreground sibling the selection was
          nearly invisible. */}
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Select test case">
        {testCases.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onSelectCase(i)}
            aria-pressed={selectedCase === i}
            className={cn(
              'rounded-md px-3 py-1 text-sm font-medium transition-colors',
              selectedCase === i
                ? 'bg-primary text-primary-foreground'
                : 'border border-border text-muted-foreground hover:bg-muted/60',
            )}
          >
            Case {i + 1}
          </button>
        ))}
      </div>

      {/* Inputs and expected output in one bordered group, so they read as a
          relationship rather than as separate stacked boxes. */}
      <div className="overflow-hidden rounded-lg border border-border">
        {inputs.map((row, i) => (
          <div key={i} className="flex gap-3 border-b border-border px-3 py-2">
            <span className="w-20 shrink-0 truncate font-mono text-xs font-semibold text-muted-foreground">
              {row.label}
            </span>
            <span className="min-w-0 flex-1 break-all font-mono text-sm">{row.value}</span>
          </div>
        ))}
        <div className="flex gap-3 bg-green-50 px-3 py-2 dark:bg-green-950/30">
          <span className="w-20 shrink-0 font-mono text-xs font-semibold text-green-700 dark:text-green-400">
            expected
          </span>
          <span className="min-w-0 flex-1 break-all font-mono text-sm text-green-900 dark:text-green-200">
            {tc?.output.trim()}
          </span>
        </div>
      </div>
    </div>
  );
}

function ResultTab({
  result,
  isJudging,
  mode,
  executionMode,
  acceptedSlot,
}: {
  result: SubmissionResult | null;
  isJudging: boolean;
  mode: 'run' | 'submit' | null;
  executionMode?: 'stdio' | 'function' | 'sql' | 'prompt';
  acceptedSlot?: React.ReactNode;
}) {
  if (isJudging) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground" aria-live="polite">
        <div
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          role="status"
          aria-label={executionMode === 'prompt' ? 'Sending prompt to the model' : 'Judging in progress'}
        />
        <span>
          {executionMode === 'prompt'
            ? 'Sending your prompt to the model, then grading its response...'
            : 'Judge is evaluating your code...'}
        </span>
      </div>
    );
  }

  if (!result) {
    return (
      <p className="text-sm text-muted-foreground">
        {executionMode === 'prompt' ? 'Run your prompt to see the model’s response and grade.' : 'Run or submit to see results.'}
      </p>
    );
  }

  // Compilation error
  if (result.verdict === 'CE') {
    return (
      <div aria-live="polite">
        <VerdictBadge verdict="CE" className="mb-3" />
        {result.error_msg && (
          <pre className="rounded bg-destructive/10 p-3 text-sm text-destructive font-mono whitespace-pre-wrap">
            {result.error_msg}
          </pre>
        )}
      </div>
    );
  }

  // Prompt Arena — rubric-itemized judge verdict, same rendering whether the
  // student ran or submitted (there's no per-testcase pass/fail concept here).
  if (executionMode === 'prompt') {
    return <PromptModeResults result={result} />;
  }

  // Run mode -- per-case results
  if (mode === 'run' && result.case_results?.length) {
    return <RunModeResults result={result} executionMode={executionMode} />;
  }

  // Submit mode -- aggregate results
  return <SubmitModeResults result={result} acceptedSlot={acceptedSlot} />;
}

function PromptModeResults({ result }: { result: SubmissionResult }) {
  let verdict: PromptVerdict | null = null;
  try {
    verdict = result.output ? (JSON.parse(result.output) as PromptVerdict) : null;
  } catch {
    verdict = null;
  }

  if (result.verdict === 'RE' || !verdict) {
    return (
      <div aria-live="polite">
        <VerdictBadge verdict={result.verdict as Verdict} className="mb-3" />
        {result.error_msg && (
          <pre className="rounded bg-destructive/10 p-3 text-sm text-destructive font-mono whitespace-pre-wrap">
            {result.error_msg}
          </pre>
        )}
      </div>
    );
  }

  return (
    <div aria-live="polite" className="space-y-4">
      <div className="flex items-center gap-3">
        <VerdictBadge verdict={result.verdict as Verdict} />
        <span className="text-sm font-semibold">
          {verdict.score} / {verdict.max_score}
        </span>
        <span className="text-sm text-muted-foreground">Graded by judge model</span>
      </div>

      <div className="flex items-start gap-1.5 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>
          Model outputs vary slightly between runs, so your score can change even without editing the prompt. A
          strong prompt is one that scores consistently high across multiple tries — if your score drops, that's a
          sign the prompt needs to constrain the model more tightly, not that the grading is inconsistent.
        </span>
      </div>

      <div
        className={cn(
          'rounded-md border p-3',
          result.verdict === 'AC'
            ? 'border-green-500/30 bg-green-500/10'
            : 'border-amber-500/30 bg-amber-500/10',
        )}
      >
        <ul className="space-y-2">
          {verdict.criteria.map((c) => (
            <li key={c.id} className="flex items-start gap-2 text-sm">
              <span
                className={c.pass ? 'text-green-600 dark:text-green-400' : 'text-red-500'}
                aria-hidden="true"
              >
                {c.pass ? '✓' : '✗'}
              </span>
              <span>
                <span className="font-medium">{c.description}</span>
                {c.note && <span className="block text-xs text-muted-foreground">{c.note}</span>}
                <span className="sr-only">{c.pass ? '(passed)' : '(not satisfied)'}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {verdict.model_outputs.length > 0 && (
        <div>
          <label className="mb-2 block text-xs font-medium text-muted-foreground">
            Conversation with the model ({verdict.model_outputs.length} test message
            {verdict.model_outputs.length === 1 ? '' : 's'})
          </label>
          <div className="space-y-3">
            {verdict.model_outputs.map((mo, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary px-3 py-2 text-sm text-primary-foreground">
                    {mo.input}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted">
                    <Sparkles className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-muted px-3 py-2 text-sm">{mo.output}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RunModeResults({
  result,
  executionMode,
}: {
  result: SubmissionResult;
  executionMode?: 'stdio' | 'function' | 'sql' | 'prompt';
}) {
  const [selectedCase, setSelectedCase] = useState(0);
  const cases = result.case_results ?? [];
  const cr = cases[selectedCase];

  // For sql-mode cases, actual/expected output are JSON-encoded row sets
  // (from the judge's json_agg-based grading). When both parse cleanly,
  // render a row-set diff table instead of the plain-text <pre> blocks.
  let sqlDiff: ReturnType<typeof diffRowSets> | null = null;
  let sqlColumns: string[] = [];
  if (executionMode === 'sql' && cr) {
    const actualRows = tryParseRowSet(cr.actual_output);
    const expectedRows = tryParseRowSet(cr.expected_output);
    if (actualRows && expectedRows) {
      sqlDiff = diffRowSets(actualRows, expectedRows);
      const colSet = new Set<string>();
      for (const row of sqlDiff) {
        for (const key of Object.keys(row.cells)) colSet.add(key);
      }
      sqlColumns = Array.from(colSet);
    }
  }

  return (
    <div aria-live="polite">
      <div className="flex items-center gap-3 mb-4">
        <VerdictBadge verdict={result.verdict as Verdict} />
        <span className="text-sm text-muted-foreground">
          {result.passed_cases}/{result.total_cases} test cases passed
        </span>
      </div>

      {/* Case selector buttons */}
      <div className="flex gap-2 mb-4" role="group" aria-label="Select result case">
        {cases.map((c, i) => (
          <Button
            key={i}
            variant={selectedCase === i ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setSelectedCase(i)}
            aria-pressed={selectedCase === i}
            className="gap-1"
          >
            <span
              className={c.verdict === 'AC' ? 'text-green-500' : 'text-red-500'}
              aria-hidden="true"
            >
              {c.verdict === 'AC' ? '✓' : '✗'}
            </span>
            Case {i + 1}
            <span className="sr-only">{c.verdict === 'AC' ? '(passed)' : '(failed)'}</span>
          </Button>
        ))}
      </div>

      {cr && sqlDiff && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Input</label>
            <pre className="rounded bg-muted px-3 py-2 font-mono text-sm whitespace-pre-wrap">
              {cr.input.trim()}
            </pre>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Result (Your Output vs Expected)
            </label>
            <Table>
              <TableHeader>
                <TableRow>
                  {sqlColumns.map((col) => (
                    <TableHead key={col}>{col}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sqlDiff.map((row, i) => (
                  <TableRow key={i}>
                    {sqlColumns.map((col) => {
                      const cell = row.cells[col];
                      return (
                        <TableCell
                          key={col}
                          className={cn(
                            'font-mono text-sm',
                            cell && !cell.matches && 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
                          )}
                        >
                          {cell && (
                            cell.matches ? (
                              formatCellValue(cell.value)
                            ) : (
                              <div className="flex flex-col gap-0.5">
                                <span>{formatCellValue(cell.value)}</span>
                                <span className="text-[11px] text-muted-foreground">
                                  expected: {formatCellValue(cell.expected)}
                                </span>
                              </div>
                            )
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {cr && !sqlDiff && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Input</label>
            <pre className="rounded bg-muted px-3 py-2 font-mono text-sm whitespace-pre-wrap">
              {cr.input.trim()}
            </pre>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Expected Output
            </label>
            <pre className="rounded bg-muted px-3 py-2 font-mono text-sm whitespace-pre-wrap">
              {cr.expected_output.trim()}
            </pre>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Your Output
            </label>
            <pre
              className={cn(
                'rounded px-3 py-2 font-mono text-sm whitespace-pre-wrap',
                cr.verdict === 'AC' ? 'bg-green-500/10' : 'bg-red-500/10'
              )}
            >
              {cr.actual_output.trim() || '(no output)'}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function SubmitModeResults({
  result,
  acceptedSlot,
}: {
  result: SubmissionResult;
  /** Rendered under the verdict on an accepted submission. Passed in rather
   *  than built here so this component stays a pure verdict renderer and does
   *  not need to know about sequences, editorials or routing. */
  acceptedSlot?: React.ReactNode;
}) {
  return (
    <div aria-live="polite">
      <div className="flex items-center gap-4 mb-4">
        <VerdictBadge verdict={result.verdict as Verdict} />
        {result.runtime_ms != null && (
          <span className="text-sm text-muted-foreground">Runtime: {result.runtime_ms}ms</span>
        )}
        {result.memory_kb != null && (
          <span className="text-sm text-muted-foreground">
            Memory: {(result.memory_kb / 1024).toFixed(1)}MB
          </span>
        )}
        <span className="text-sm text-muted-foreground">
          {result.passed_cases}/{result.total_cases} passed
        </span>
      </div>
      {result.error_msg && (
        <pre className="rounded bg-destructive/10 p-3 text-sm text-destructive font-mono whitespace-pre-wrap">
          {result.error_msg}
        </pre>
      )}
      {result.verdict === 'AC' && acceptedSlot && <div className="mt-4">{acceptedSlot}</div>}
    </div>
  );
}
